import { FolderDetail, RecipeInputField, RecipeItem, WildcardDetail } from "@/api/sdk"
import IconButton from "@/components/IconButton"
import { COMFY_RECIPE_ID } from "@/components/Tools/ComfyUIRecipe/ComfyUIRecipeBuilder/ComfyUIDetail"
import { TRY_STYLE_RECIPE } from "@/components/Tools/Style/StyleDetail/StyleDetail"
import { ArrowSquareLeftIcon, ChevronDownIcon, InfoIcon, RefreshIcon, ShareIcon8 } from "@/components/shared/icons"
import { useScreen } from "@/hooks"
import { useAuth } from "@/providers/authContext"
import { cn } from "@/utils/cn"
import classNames from "classnames"
import { motion } from "framer-motion"
import dynamic from "next/dynamic"
import React, { useRef, useState } from "react"
import { UseFormReturn } from "react-hook-form"
import { RemoveScroll } from "react-remove-scroll"
import DrawerSelectFolder from "../../Workflows/WorkflowsChaining/DrawerSelectFolder"
import BackButton from "@/components/BackButton"
import { useBrowserRouter } from "@/providers/BrowserRouterProvider"
import { getHasClientHistory } from "@/stores/ClientHistoryStore"
import { googleAnalytics } from "@/lib/gtag"
import useCustomRouter from "@/hooks/useCustomRouter"
import useModalStore from "@/lib/store"
import RecipeSelect from "../../Workflows/WorkflowsChaining/RecipeSelect"
import useAutoSizeTextArea from "@/hooks/useAutoSizeTextArea"
import Tooltip from "@/components/Tooltip"
import { RecipeCreateType } from "@/utils/task"
const DynamicFormRecipe = dynamic(() => import("./FormDetail"), { ssr: false })

interface SettingRecipeProps {
  isOpenSideBar: boolean
  sendRecipe: string
  setSendRecipe: (recipe: string) => void
  recipeInputs: (RecipeInputField & { isChecked?: boolean })[]
  setRecipeInputs: React.Dispatch<React.SetStateAction<(RecipeInputField & { isChecked?: boolean })[]>>
  setIsOpenSidebar: React.Dispatch<React.SetStateAction<boolean>>
  isLoadingCreate: boolean
  form: UseFormReturn<RecipeCreateType, any, undefined>
  recipe?: RecipeItem
  wildcardsState: WildcardDetail[]
  loraIds: string[]
  onResetDefaultSettings: () => void
}

const SettingRecipe = ({
  isOpenSideBar,
  setIsOpenSidebar,
  sendRecipe,
  onResetDefaultSettings,
  setSendRecipe,
  recipeInputs,
  loraIds,
  isLoadingCreate,
  wildcardsState,
  setRecipeInputs,
  form,
  recipe,
}: SettingRecipeProps) => {
  const { width } = useScreen()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const [isOpenRecipes, setIsOpenRecipes] = useState(false)
  const [folder, setFolder] = useState<FolderDetail>()
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeItem>()
  const { user } = useAuth()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const recipeContainerRef = useRef<HTMLDivElement>(null)
  const router = useCustomRouter()
  const { back } = useBrowserRouter()
  const setSharingData = useModalStore(state => state.setSharingData)

  const currentRecipe = selectedRecipe ?? recipe

  const handleShare = () => {
    if (!recipe || recipe.isPublic) return

    googleAnalytics.handleCategoryEvent({
      action: "click",
      params: {
        action: "Share Recipe",
        recipe_id: recipe.id,
        recipe_name: recipe.name,
      },
    })

    setSharingData({
      id: recipe.id,
      name: recipe.name ?? recipe.id,
      type: "recipe",
      workspaceId: recipe.workspace?.id,
      creator: {
        ...recipe.creator,
        uid: recipe.creator?.uid ?? "",
      },
      link: `${window.location.origin}/workspace/tools/recipes/${recipe.id}`,
    })
  }

  const handleBack = () => {
    googleAnalytics.handleCategoryEvent({
      action: "click",
      params: {
        action: "Back",
      },
    })

    const hasHistory = getHasClientHistory()

    if (hasHistory) {
      back()
    } else {
      router.push("/workspace/tools/recipes")
    }
  }

  useAutoSizeTextArea(textareaRef.current, currentRecipe?.description ?? "", !!currentRecipe)

  const {
    formState: { errors },
  } = form

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        key={isOpenSideBar ? "open" : "close"}
        transition={{ duration: 0.5, delay: 0.15 }}
        onClick={e => {
          e.stopPropagation()
          setIsOpenSidebar(prev => !prev)
        }}
        className={classNames("fixed flex top-[4.75rem] z-[24] cursor-pointer rounded-md", {
          "rotate-180 left-2": !isOpenSideBar,
          "left-[88vw] md:left-80 lg:left-[18.25rem]": isOpenSideBar,
        })}
      >
        <ArrowSquareLeftIcon width={24} height={24} />
      </motion.div>
      <motion.div
        className={cn(
          "border-l border-r border-atherGray-800 bg-atherGray-900",
          "fixed top-[0] lg:sticky md:top-[4.5rem] left-0 overflow-hidden flex flex-col z-[24] md:z-[2] pb-[5.15rem] md:pb-0 height-full-screen md:h-[calc((var(--vh,1vh)*100)-4.5rem-1px)]",
          "transition-all duration-300 ease-in-out",
          {
            "w-0": !isOpenSideBar,
            "w-[88vw] md:w-80": isOpenSideBar,
          },
        )}
      >
        <div className="flex flex-col w-[88vw] md:w-80 bg-atherGray-900 overflow-hidden flex-1">
          <RemoveScroll
            enabled={width < 768 && isOpenSideBar}
            style={{
              animationDelay: "0s",
              overflow: "hidden",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
            removeScrollBar
          >
            <div
              className="overflow-auto z-[1] flex-1 flex flex-col"
              ref={sidebarRef}
              style={{
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div className="relative p-4 border-b border-atherGray-700" ref={recipeContainerRef}>
                <div className="p-4 absolute pointer-events-none inset-0 w-full h-full">
                  <div className="w-full h-full select-recipe" />
                </div>

                <div className="w-full mb-4 flex items-center space-x-2">
                  <BackButton
                    className="rounded-lg text-sm py-1.5 px-3 min-h-0"
                    label="Back"
                    onClick={() => handleBack()}
                  />
                  {recipe && user && recipe.capabilities?.canShare && !recipe.isPublic && (
                    <IconButton
                      colorScheme="secondary"
                      title="Share"
                      className="font-semibold text-xs p-2"
                      onClick={e => {
                        e.stopPropagation()
                        handleShare()
                      }}
                    >
                      <ShareIcon8 width={16} height={16} />
                    </IconButton>
                  )}
                  {recipe && (
                    <Tooltip
                      side="bottom"
                      trigger={
                        <IconButton
                          colorScheme="secondary"
                          className="font-semibold text-xs p-2"
                          onClick={e => {
                            e.stopPropagation()
                            onResetDefaultSettings()
                          }}
                        >
                          <RefreshIcon width={16} height={16} className="rotate-180" />
                        </IconButton>
                      }
                    >
                      Reset default settings
                    </Tooltip>
                  )}
                </div>

                <p className="mb-1 text-sm text-atherGray-300">Select a recipe</p>
                <div
                  className={classNames(
                    "bg-atherGray-800 p-2 text-sm rounded-lg flex items-center overflow-hidden cursor-pointer border",
                    {
                      "border-atherGray-800": !isOpenRecipes,
                      "border-atherPurple-500": isOpenRecipes,
                    },
                  )}
                  onClick={e => {
                    e.stopPropagation()
                    setIsOpenRecipes(prev => !prev)
                  }}
                >
                  <p className="line-clamp-1 flex-1">{currentRecipe?.name ?? ""}</p>
                  <div className="text-atherGray-300">
                    <ChevronDownIcon className="-rotate-90" width={14} height={14} />
                  </div>
                </div>
                {currentRecipe?.description && (
                  <div className="flex items-start mt-2">
                    <InfoIcon className="text-atherGray-600 mr-2" width={14} height={14} />
                    <textarea
                      ref={textareaRef}
                      disabled={true}
                      style={{
                        height: "24px",
                      }}
                      readOnly={true}
                      value={currentRecipe?.description}
                      className={classNames("resize-none text-xs text-atherGray-300 flex-1 bg-transparent")}
                    />
                  </div>
                )}
                <RecipeSelect
                  isOpen={isOpenRecipes}
                  selectedRecipe={selectedRecipe ?? { id: sendRecipe, isPublic: true }}
                  setSelectedRecipe={(recipe, _param) => {
                    setSelectedRecipe(recipe)
                    setSendRecipe(recipe.id)
                  }}
                  onClose={() => setIsOpenRecipes(false)}
                />
              </div>
              <div
                className={classNames("p-4 flex-1 flex flex-col ", {
                  hidden: recipe?.id === COMFY_RECIPE_ID || recipe?.id === TRY_STYLE_RECIPE,
                })}
              >
                <DynamicFormRecipe
                  loraIds={loraIds}
                  wildcardsState={wildcardsState}
                  recipeInputs={recipeInputs}
                  setRecipeInputs={setRecipeInputs}
                  form={form}
                  recipe={recipe}
                  hideOutputFolder
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 p-4 items-stretch relative border-t border-atherGray-800">
              <div className="absolute inset-0 w-full h-full pointer-events-none p-4">
                <div className="w-full h-full recipe-save-or-generate" />
              </div>
              <div
                className={classNames({
                  hidden: !user || recipe?.id === COMFY_RECIPE_ID || recipe?.id === TRY_STYLE_RECIPE,
                })}
              >
                <p className="mb-1 font-semibold text-sm">Save the output in</p>
                <div id="folderId">
                  <DrawerSelectFolder
                    title="Save the output in"
                    selected={folder}
                    setSelected={v => {
                      setFolder(v)
                      form.setValue("folderId", v?.id)
                    }}
                    placeHolder="Recipe Images"
                  />
                  {errors.folderId?.message && (
                    <span className="mt-1 text-xs text-red-500">{errors.folderId?.message}</span>
                  )}
                </div>
              </div>
              <IconButton
                isLoading={isLoadingCreate}
                type="submit"
                className={classNames("p-2 w-full font-semibold", {
                  hidden: recipe?.id === COMFY_RECIPE_ID || recipe?.id === TRY_STYLE_RECIPE,
                })}
              >
                Generate
              </IconButton>
            </div>
          </RemoveScroll>
        </div>
      </motion.div>
    </>
  )
}

export default SettingRecipe
